/**
 * 🔔 ProfileNotificationsFeed — inline notifications panel on the
 * personal profile page.
 *
 * Owner directive Feb 2026: "Notifications are supposed to show up on
 * your personal profile. And items should be checked off when looking
 * at it."
 *
 * Behavior:
 *   • Loads the latest 15 notifications on mount.
 *   • Highlights unread items with a purple glow (same visual as the
 *     bell dropdown + /notifications page).
 *   • Auto-marks everything as read after 1.5s of visibility — the
 *     brief delay lets the player register which ones were new, then
 *     the dots clear and the item fades to the "read" tint.
 *   • Individual click still marks that one read + follows its link.
 *   • A link to the full /notifications page for anything beyond 15.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, Check, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Cookie-based auth (same as the rest of the app).
axios.defaults.withCredentials = true;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function ProfileNotificationsFeed() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [markedBatch, setMarkedBatch] = useState(0);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications?limit=15`);
      setItems(data.items || data.notifications || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ✅ Auto-check-off on view (owner directive). The 1.5s delay lets
  // the player register the fresh purple glow before items quietly flip
  // to "read" — mirrors Instagram/Discord. Idempotent: `markedBatch` is
  // only set when the server actually marks something, so React Strict
  // Mode's double-mount can run the effect twice safely.
  useEffect(() => {
    if (loading || items.length === 0) return;
    const unreadCount = items.filter((x) => !x.read).length;
    if (unreadCount === 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/notifications/read-all`);
        if (cancelled) return;
        setItems((prev) => prev.map((x) => ({ ...x, read: true })));
        const marked = Number(data?.marked ?? unreadCount) || 0;
        if (marked > 0) {
          setMarkedBatch((prev) => prev || marked);
          try {
            window.dispatchEvent(new CustomEvent("omnimuscle:notifications-read", {
              detail: { marked },
            }));
          } catch { /* noop */ }
        }
      } catch { /* silent */ }
    }, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [loading, items]);

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await axios.post(`${API}/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch { /* silent */ }
    }
    if (n.link) navigate(n.link);
  };

  const unreadCount = items.filter((x) => !x.read).length;

  return (
    <section
      className="mt-10 rounded-xl border border-purple-800/40 bg-gradient-to-br from-purple-950/40 via-black to-neutral-950 p-4"
      data-testid="profile-notifications-feed"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-display text-2xl text-white flex items-center gap-2">
          <Bell className="h-5 w-5 text-purple-300" />
          Notifications
          {unreadCount > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center rounded-full bg-purple-500 text-black text-[10px] font-black px-2 py-0.5"
              data-testid="profile-notif-unread-badge"
            >
              {unreadCount}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => navigate("/notifications")}
          data-testid="profile-notif-view-all"
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-purple-300 hover:text-purple-100 transition-colors"
        >
          View all <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {markedBatch > 0 && (
        <div
          className="mb-3 flex items-center gap-1.5 rounded-lg border border-emerald-800/40 bg-emerald-950/30 px-3 py-1.5 text-[11px] text-emerald-200"
          data-testid="profile-notif-auto-marked"
        >
          <Check className="h-3.5 w-3.5" />
          Checked off {markedBatch} new {markedBatch === 1 ? "notification" : "notifications"} for you.
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-purple-300/70 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-purple-900/30 bg-black/40 p-6 text-center">
          <div className="text-2xl mb-1">🌱</div>
          <div className="text-sm text-purple-200">Nothing yet.</div>
          <div className="text-[11px] text-purple-400/70 mt-1">
            Hatch an egg, clear a run, or get a shout-out — this will fill up fast.
          </div>
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="profile-notif-list">
          {items.map((n) => (
            <li
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`profile-notif-${n.id}`}
              className={`flex items-start gap-3 rounded-lg border p-2.5 cursor-pointer transition-all duration-500 ${
                n.read
                  ? "border-purple-900/20 bg-purple-950/10 opacity-70 hover:opacity-100"
                  : "border-purple-500/50 bg-purple-900/30 shadow-[0_0_18px_-10px_rgba(168,85,247,0.6)]"
              }`}
            >
              <div className="text-lg leading-none mt-0.5">{n.icon || "🔔"}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${n.read ? "text-purple-200" : "text-purple-50"} truncate`}>
                    {n.title}
                  </span>
                  {!n.read && (
                    <span
                      className="h-2 w-2 rounded-full bg-purple-400 flex-shrink-0"
                      data-testid={`profile-notif-unread-dot-${n.id}`}
                    />
                  )}
                  {n.read && (
                    <Check
                      className="h-3 w-3 text-emerald-400/70 flex-shrink-0"
                      data-testid={`profile-notif-checked-${n.id}`}
                    />
                  )}
                </div>
                {n.body && (
                  <div className="mt-0.5 text-xs text-purple-300/90 line-clamp-2">
                    {n.body}
                  </div>
                )}
                <div className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-purple-400/60">
                  <span>{n.kind}</span>
                  <span>·</span>
                  <span>{timeAgo(n.created_at)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
